"use client";

import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, EyeOff, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ConnectionConfig } from "../../connection-studio";

interface SMBConfigFormProps {
  formData: ConnectionConfig;
  onChange: (updates: Partial<ConnectionConfig>) => void;
}

const DEFAULT_EXTENSIONS = [
  ".pdf", ".docx", ".xlsx", ".txt", ".md", ".csv", ".json", ".yaml", ".yml",
];

export function SMBConfigForm({ formData, onChange }: SMBConfigFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showCrawlSettings, setShowCrawlSettings] = useState(false);

  const config = formData.config || {};
  const authConfig = formData.auth_config || {};

  const updateConfig = (key: string, value: unknown) => {
    onChange({ config: { ...config, [key]: value } });
  };

  const updateAuth = (key: string, value: unknown) => {
    onChange({ auth_config: { ...authConfig, [key]: value } });
  };

  const crawlExtensions = (config.crawl_extensions as string[]) || DEFAULT_EXTENSIONS;

  const toggleExtension = (ext: string) => {
    const current = new Set(crawlExtensions);
    if (current.has(ext)) {
      current.delete(ext);
    } else {
      current.add(ext);
    }
    updateConfig("crawl_extensions", Array.from(current));
  };

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 space-y-4">
        <p className="text-xs font-medium uppercase tracking-wider text-white/50">
          Server Connection
        </p>

        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2 space-y-2">
            <Label className="text-white/80">
              Server Address <span className="text-rose-400">*</span>
            </Label>
            <Input
              value={(config.server as string) || ""}
              onChange={(e) => updateConfig("server", e.target.value)}
              placeholder="192.168.1.100 or fileserver.local"
              className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-white/80">Port</Label>
            <Input
              type="number"
              value={(config.port as number) || 445}
              onChange={(e) => updateConfig("port", parseInt(e.target.value) || 445)}
              className="border-white/10 bg-white/5 text-white"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-white/80">
              Share Name <span className="text-rose-400">*</span>
            </Label>
            <Input
              value={(config.share_name as string) || ""}
              onChange={(e) => updateConfig("share_name", e.target.value)}
              placeholder="SharedDocs"
              className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-white/80">Base Path</Label>
            <Input
              value={(config.base_path as string) || ""}
              onChange={(e) => updateConfig("base_path", e.target.value)}
              placeholder="/ (optional subfolder)"
              className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
            />
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4 space-y-4">
        <p className="text-xs font-medium uppercase tracking-wider text-white/50">
          Authentication
        </p>

        <div className="space-y-2">
          <Label className="text-white/80">Domain</Label>
          <Input
            value={(config.domain as string) || "WORKGROUP"}
            onChange={(e) => updateConfig("domain", e.target.value)}
            placeholder="WORKGROUP"
            className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
          />
          <p className="text-xs text-white/40">NTLM domain (default: WORKGROUP)</p>
        </div>

        <div className="space-y-2">
          <Label className="text-white/80">
            Username <span className="text-rose-400">*</span>
          </Label>
          <Input
            value={(authConfig.username as string) || ""}
            onChange={(e) => updateAuth("username", e.target.value)}
            placeholder="Enter username"
            className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
          />
        </div>

        <div className="space-y-2">
          <Label className="text-white/80">
            Password <span className="text-rose-400">*</span>
          </Label>
          <div className="relative">
            <Input
              type={showPassword ? "text" : "password"}
              value={(authConfig.password as string) || ""}
              onChange={(e) => updateAuth("password", e.target.value)}
              placeholder="Enter password"
              className="border-white/10 bg-white/5 pr-10 text-white placeholder:text-white/40"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 text-white/40 hover:text-white"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-white/10 bg-white/[0.02]">
        <button
          type="button"
          onClick={() => setShowCrawlSettings(!showCrawlSettings)}
          className="flex w-full items-center justify-between p-4 text-left"
        >
          <p className="text-xs font-medium uppercase tracking-wider text-white/50">
            Crawl Settings
          </p>
          {showCrawlSettings ? (
            <ChevronUp className="h-4 w-4 text-white/40" />
          ) : (
            <ChevronDown className="h-4 w-4 text-white/40" />
          )}
        </button>

        {showCrawlSettings && (
          <div className="space-y-4 border-t border-white/10 p-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-white/80">Max Depth</Label>
                <Input
                  type="number"
                  value={(config.max_depth as number) || 10}
                  onChange={(e) => updateConfig("max_depth", parseInt(e.target.value) || 10)}
                  min={1}
                  max={50}
                  className="border-white/10 bg-white/5 text-white"
                />
                <p className="text-xs text-white/40">Directory depth limit</p>
              </div>

              <div className="space-y-2">
                <Label className="text-white/80">Max File Size (MB)</Label>
                <Input
                  type="number"
                  value={(config.max_file_size_mb as number) || 10}
                  onChange={(e) => updateConfig("max_file_size_mb", parseInt(e.target.value) || 10)}
                  min={1}
                  max={100}
                  className="border-white/10 bg-white/5 text-white"
                />
                <p className="text-xs text-white/40">Skip files larger than this</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white/80">File Extensions to Index</Label>
              <div className="flex flex-wrap gap-1.5">
                {DEFAULT_EXTENSIONS.map((ext) => (
                  <Badge
                    key={ext}
                    variant="outline"
                    className={cn(
                      "cursor-pointer text-xs",
                      crawlExtensions.includes(ext)
                        ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                        : "border-white/10 text-white/40 hover:text-white/60"
                    )}
                    onClick={() => toggleExtension(ext)}
                  >
                    {ext}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-white/80">Exclude Patterns</Label>
              <Input
                value={(config.exclude_patterns_str as string) || ".*,__*,$RECYCLE.BIN"}
                onChange={(e) => {
                  updateConfig("exclude_patterns_str", e.target.value);
                  updateConfig(
                    "exclude_patterns",
                    e.target.value.split(",").map((s: string) => s.trim()).filter(Boolean)
                  );
                }}
                placeholder=".*,__*,node_modules"
                className="border-white/10 bg-white/5 text-white placeholder:text-white/40"
              />
              <p className="text-xs text-white/40">Comma-separated patterns to skip</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
